import { Observable, from, of, EMPTY, timer } from 'rxjs';
import { map, filter, switchMap, catchError, distinctUntilChanged, tap } from 'rxjs/operators';

import { CookieBannerEvents } from '../actions';
import { CookieBannerStore, CookieFirstWindow, CookieScan } from '../types';

// Cache constants
const CACHE_KEY = 'w-cookiefirst-cookie-categories';
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 hour in milliseconds

// Type for the combined cache object
interface CachedScanData {
    timestamp: number;
    data: CookieScan;
}

/**
 * Fetch the latest cookie scan data from CookieFirst.
 *
 * This allows us to use the data from CookieFirst to determine which constent
 * categories a cdookie belongs to, without hardcoding the value.
 *
 * The data is cached in localStorage to avoid unnecessary network requests.
 */
export function fetchLatestScanDataEpic(
    _action$: Observable<any>,
    state$: Observable<CookieBannerStore>,
): Observable<any> {
    return state$.pipe(
        // Wait for the Cookie Banner to be initialized
        map((state) => state.cookieBanner?.initialized === true),
        distinctUntilChanged(),
        filter((initialized) => initialized),
        switchMap(() => {
            return state$.pipe(
                map((state) => state.cookieBanner),
                switchMap((cookieBanner) => {
                    const currentScan = cookieBanner?.cookieScan;
                    let cachedData: CachedScanData | null = null;

                    try {
                        const cacheStr = localStorage.getItem(CACHE_KEY);

                        if (cacheStr) {
                            cachedData = JSON.parse(cacheStr) as CachedScanData;
                        }
                    } catch (error) {
                        console.error('Error reading cached scan data:', error);
                    }

                    const now = Date.now();
                    const cachedTimestamp = cachedData?.timestamp || 0;
                    const isCacheValid = currentScan && now - cachedTimestamp < CACHE_EXPIRY;

                    if (isCacheValid) {
                        return EMPTY;
                    }

                    return timer(500).pipe(
                        switchMap(() => {
                            const cookieFirst = (window as CookieFirstWindow)?.CookieFirst;

                            if (
                                !cookieFirst ||
                                !cookieFirst?.fetchLatestScan ||
                                typeof cookieFirst?.fetchLatestScan !== 'function'
                            ) {
                                return EMPTY;
                            }

                            return from(cookieFirst.fetchLatestScan()).pipe(
                                // Parse the response data
                                map((response) => {
                                    return {
                                        cookies: response.cookies.map((cookie) => ({
                                            category: cookie.cat,
                                            description: cookie.desc,
                                            domain: cookie.domain,
                                            expiration: cookie.expiration_time,
                                            name: cookie.name,
                                            type: cookie.type,
                                        })),
                                    } as CookieScan;
                                }),

                                // Save the data to localstorage.
                                tap((data) => {
                                    try {
                                        const cacheObject = {
                                            timestamp: Date.now(),
                                            data: data,
                                        };

                                        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObject));
                                    } catch (error) {
                                        console.error('Error caching scan data:', error);
                                    }
                                }),

                                // Emit the event
                                map((data) => ({
                                    type: CookieBannerEvents.LATEST_SCAN,
                                    payload: data,
                                })),

                                catchError((error) => {
                                    console.error('Error fetching cookie scan data:', error);

                                    try {
                                        const cacheItem = localStorage.getItem(CACHE_KEY);

                                        if (cacheItem) {
                                            const parsedData = JSON.parse(cacheItem);

                                            return of({
                                                type: CookieBannerEvents.LATEST_SCAN,
                                                payload: parsedData.data,
                                            });
                                        }
                                    } catch (e) {
                                        console.error('Error reading cached data:', e);
                                    }

                                    return EMPTY;
                                }),
                            );
                        }),
                    );
                }),
            );
        }),
    );
}

export default fetchLatestScanDataEpic;
